import { memo, useMemo, useState } from 'react'

import { EditRounded } from '@mui/icons-material'
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'

import { DateTime } from 'luxon'

import { useSmallScreen } from '@common/utils'
import CustomDateModal from '@portal/pages/Dashboards/SmokeMetrics/CustomDateModal'

import { AVAILABLE_INTERVALS } from '../constants'
import RangeInputPopover from './RangeInputPopover'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.selectedInterval
 * @param {string} [props.selectedInterval.label]
 * @param {string} [props.selectedInterval.range]
 * @param {string} [props.selectedInterval.defaultRange]
 * @param {Function} props.setSelectedInterval
 * @param {Object} props.availableIntervals
 * @param {boolean} props.disabled
 */
const RangeSelector = memo(
  ({ selectedInterval, dateRangeValue, goLiveDate, onChange, disabled }) => {
    const isSmallScreen = useSmallScreen()
    const [openCustomDate, setOpenCustomDate] = useState(false)

    const [rangePopoverData, setRangePopoverData] = useState(null)

    const rangeText = useMemo(() => {
      switch (selectedInterval.id) {
        case AVAILABLE_INTERVALS.monthly.id: {
          const range = selectedInterval.range ?? selectedInterval.defaultRange
          return `${range} ${range === 1 ? 'Month' : 'Months'}`
        }
        case AVAILABLE_INTERVALS.weekly.id: {
          const range = selectedInterval.range ?? selectedInterval.defaultRange
          return `${range} ${range === 1 ? 'Week' : 'Weeks'}`
        }
        case AVAILABLE_INTERVALS.custom.id: {
          const format = 'yyyy-MM-dd'
          const start = dateRangeValue?.[0]?.[0]?.toFormat(format)
          const end = dateRangeValue?.[dateRangeValue.length - 1]?.[1]?.toFormat(format)
          return start && end ? `${start} - ${end}` : '--'
        }

        default:
          throw new Error(`Unexpected interval: ${selectedInterval.id}`)
      }
    }, [selectedInterval, dateRangeValue])

    return (
      <>
        <CustomDateModal
          open={openCustomDate}
          minDate={goLiveDate ? DateTime.fromISO(goLiveDate) : null}
          onCancel={() => setOpenCustomDate(false)}
          onSave={(dateRange) => {
            const interval = { ...AVAILABLE_INTERVALS.custom, ranges: [dateRange] }
            onChange(interval)
            setOpenCustomDate(false)
          }}
        />
        <RangeInputPopover
          data={rangePopoverData}
          goLiveDate={goLiveDate}
          onClose={() => setRangePopoverData(null)}
          onChange={onChange}
          disabled={disabled}
        />
        <Box
          display="flex"
          flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
          gap={1}
          alignItems="end"
          width={1}
        >
          <Stack
            direction="column"
            spacing={1}
            ml={1}
            width={isSmallScreen.mobile ? 1 : null}
          >
            <Typography variant="body1">Range</Typography>
            <ToggleButtonGroup
              color="primary"
              variant="outlined"
              disabled={disabled}
              sx={{ overflow: 'auto' }}
            >
              {Object.keys(AVAILABLE_INTERVALS).map((key) => {
                const interval = AVAILABLE_INTERVALS[key]
                const selected = selectedInterval.id === interval.id
                return (
                  <ToggleButton
                    key={selectedInterval.id}
                    value={interval}
                    selected={selected}
                    onClick={(event, selectedValue) => {
                      if (selectedValue.id === AVAILABLE_INTERVALS.custom.id) {
                        setOpenCustomDate(true)
                      } else {
                        setRangePopoverData({
                          interval: selectedValue,
                          anchor: event.currentTarget,
                        })
                      }
                    }}
                    sx={{
                      py: 0.8,
                      width: isSmallScreen.mobile ? 1 : null,
                      pointerEvents: selected ? 'none' : null,
                    }}
                  >
                    {interval.label.toUpperCase()}
                  </ToggleButton>
                )
              })}
            </ToggleButtonGroup>
          </Stack>
          <Stack
            direction="column"
            spacing={1}
            ml={1}
            width={isSmallScreen.mobile ? 1 : null}
          >
            <Typography variant="body1">Duration</Typography>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              width={isSmallScreen.mobile ? 1 : 260}
              sx={{
                border: 1,
                borderRadius: 1,
                borderColor: '#e0e0e0',
                pl: 2,
                pr: 1,
                py: 0.6,
                mt: 1,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width={1}
                gap={2}
              >
                <Typography variant="body2" color="grey" sx={{ userSelect: 'none' }}>
                  {rangeText}
                </Typography>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(event) => {
                    if (selectedInterval.id === AVAILABLE_INTERVALS.custom.id) {
                      setOpenCustomDate(true)
                    } else {
                      setRangePopoverData({
                        interval: selectedInterval,
                        anchor: event.currentTarget,
                        isEdit: true,
                      })
                    }
                  }}
                >
                  <EditRounded
                    color={disabled ? 'grey' : 'primary'}
                    sx={{ fontSize: 18 }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </>
    )
  },
)

export default RangeSelector
